import React, { useMemo } from 'react';
import { eagerImages, zoneOf, type ElementNodeDto, type ElementTreeDto, type ElementType, type ThemeStylesDto } from '@xshop/shared';
import {
  ElementRenderProvider,
  type ElementComponent, type ElementMarks, type ElementRenderContextValue, type PresetRenderer
} from './elementRenderContext';
import { ElementNodeView, ROOT_PATH } from './ElementNode';
import type { StoreFacts } from './storeFacts';
import { EagerImagesContext } from './leadImage';

// The one component a page mounts. Everything the engine needs from outside arrives here and nowhere
// else: the store's tokens, the theme's preset drawing function, the renderers other folders own, and
// whether this is the builder. A mount wires those four; nothing below this file imports a theme.
//
// There is no stylesheet element here on purpose. Each node's rule is written by the style engine as the
// node mounts, in an insertion effect, so it lands before the browser paints and disappears with the
// element. A page rendered outside a browser collects the same rules with `renderElementStyleSheet`.

/** What a page hands the engine to draw itself. */
export type ElementTreeViewProps = {
  tree: ElementTreeDto;
  /** The store's tokens — its own where it has repainted any, otherwise the ones the theme ships. */
  theme: ThemeStylesDto;
  /** Draws a theme's own component for a `section` carrying a `preset`. */
  renderPreset?: PresetRenderer;
  /** Renderers for the types this folder does not own — commerce, interactive — keyed by type. */
  renderers?: Readonly<Partial<Record<ElementType, ElementComponent>>>;
  /** True only inside the builder. A shopper's page is drawn with this off and carries no marks. */
  editing?: boolean;
  motionPreview?: boolean;
  /** The facts a bound prop reads. Left out, every binding goes unanswered and the typed value stands. */
  store?: StoreFacts | null;
  /** The catalog's marks for the canvas, when the mount has read it; a node wears its id and type without. */
  marks?: ElementMarks | null;
};

/** What the skip link says, in each language a store may be served in. */
const SKIP_LABELS: Readonly<Record<'ar' | 'fr' | 'en', string>> = { en: 'Skip to content', fr: 'Aller au contenu', ar: 'تخطي إلى المحتوى' };

// Draws one part of the page's roots, in the order they stand.
const RootList: React.FC<{ roots: readonly ElementNodeDto[] }> = ({ roots }) => (
  <>{roots.map((node) => <ElementNodeView key={node.id} node={node} path={ROOT_PATH} />)}</>
);

// Draws the roots as a page's landmarks: a skip link, the header, the page's own content in <main>, the footer.
const PageLandmarks: React.FC<{ roots: readonly ElementNodeDto[]; editing: boolean; language?: 'ar' | 'fr' | 'en' }> = ({ roots, editing, language }) => {
  // A theme that tagged one of its own sections as the main landmark keeps it; the wrapper is then a plain box.
  const Main = roots.some((root) => root.props.tag === 'main') ? 'div' : 'main';
  return (
    <>
      {editing ? null : <a className="xe-skip-link" href="#main-content">{SKIP_LABELS[language ?? 'en']}</a>}
      <RootList roots={roots.filter((root) => zoneOf(root) === 'header')} />
      <Main id="main-content" className="xe-main" tabIndex={-1}><RootList roots={roots.filter((root) => zoneOf(root) === 'template')} /></Main>
      <RootList roots={roots.filter((root) => zoneOf(root) === 'footer')} />
    </>
  );
};

/** No extra renderers, hoisted so a tree drawn without any is not given a new object on every render. */
const NO_RENDERERS: Readonly<Partial<Record<ElementType, ElementComponent>>> = {};

// Draws a page: every root walked to its leaves, each node wearing the class the style engine wrote it.
export const ElementTreeView: React.FC<ElementTreeViewProps> = ({ tree, theme, renderPreset, renderers, editing, motionPreview, marks, store }) => {
  const value = useMemo<ElementRenderContextValue>(() => ({
    theme,
    renderPreset: renderPreset ?? null,
    renderers: renderers ?? NO_RENDERERS,
    editing: editing === true,
    motionPreview: motionPreview === true,
    marks: marks ?? null,
    store: store ?? null
  }), [theme, renderPreset, renderers, editing, motionPreview, marks, store]);
  const eager = useMemo(() => eagerImages(tree.roots), [tree]);
  return (
    <ElementRenderProvider value={value}>
      <EagerImagesContext.Provider value={eager}>
        <PageLandmarks roots={tree.roots} editing={editing === true} language={store?.language} />
      </EagerImagesContext.Provider>
    </ElementRenderProvider>
  );
};
